<!--
	SPDX-FileCopyrightText: Ferdinand Thiessen <opensource@fthiessen.de>
	SPDX-License-Identifier: AGPL-3.0-or-later
-->
<template>
	<article :class="$style.wrapper">
		<header :class="$style.header">
			<NcButton
				:aria-label="t('security_guard', 'Back')"
				type="tertiary"
				@click="emit('back')">
				<template #icon>
					<NcIconSvgWrapper :path="mdiArrowLeft" />
				</template>
			</NcButton>
			<SeverityIcon :severity="entry.severity" :visual="true" />
			<h3>{{ entry?.name }}</h3>
		</header>
		<p :class="$style.severity">
			{{ severityName }}
		</p>
		<NcRichText
			:class="$style.text"
			:arguments="richArguments"
			:use-markdown="true"
			:text="
				entry?.description ??
				t('security_guard', 'No further description available.')
			" />
		<div>
			<NcButton
				type="primary"
				:disabled="!entry?.linkToDoc"
				:href="entry.linkToDoc"
				:title="
					entry?.linkToDoc
						? undefined
						: t('security_guard', 'No documentation available')
				"
				:wide="true">
				<template #icon>
					<NcIconSvgWrapper :path="mdiBook" />
				</template>
				{{ t('security_guard', 'Read the documentation') }}
			</NcButton>
		</div>
	</article>
</template>

<script setup lang="ts">
import type { ISetupCheck } from '../../types.ts'

import { SEVERITY_NAMES } from '../../constants.ts'
import { useRichArguments } from '../../composables/useRichArguments.ts'
import { mdiArrowLeft, mdiBook } from '@mdi/js'
import { translate as t } from '@nextcloud/l10n'
import { computed } from 'vue'

import NcButton from '@nextcloud/vue/dist/Components/NcButton.js'
import NcIconSvgWrapper from '@nextcloud/vue/dist/Components/NcIconSvgWrapper.js'
import NcRichText from '@nextcloud/vue/dist/Components/NcRichText.js'
import SeverityIcon from '../SeverityIcon.vue'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
	entry: ISetupCheck
}>()

const emit = defineEmits<{
	(e: 'back'): void
}>()

const { richArguments } = useRichArguments(
	computed(() => props.entry.descriptionParameters),
)

const severityName = computed(
	() => SEVERITY_NAMES[props.entry.severity] ?? SEVERITY_NAMES.unknown,
)
</script>

<style module lang="scss">
@import '../../styles/link';

.wrapper {
	display: flex;
	flex: 1 0;
	flex-direction: column;
	gap: 16px;
	width: 100%;

	& > * {
		box-sizing: border-box;
		padding-inline-end: 22px; // add missing padding (see DetailsModal)
	}
}

.header {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;

	h3 {
		font-size: 18px;
		font-weight: bold;
		line-height: 1.5;
	}
}

.severity {
	font-size: 16px;
	font-weight: bold;
	line-height: 1.5;

	margin-block: 1em 0.5em;
}

.text {
	@include linkStyle;
	overflow: scroll;

	// To make all links look consistent, as NcRichText has custom styles
	a:not(:hover) {
		text-decoration: none !important;
	}
	flex: 1 0;
}
</style>
